import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { store } from ".";
import { CalculatorApi, CalculatorApiApiCalculatorGetRequest, GroupApi, GroupEventModel, GroupServiceGetResponse, HighRiskApi, HighRiskApiApiHighRiskRaisePostRequest, HighRiskApiApiHighRiskRemovePostRequest, HighRiskApiApiHighRiskReviewPostRequest, HighRiskModel, RateReliefCalculatorServiceResponse, ReRateModel, ReRatesApi, ReRatesApiApiReRatesRaisePostRequest, Tier2Api, Tier2ApiApiTier2CompletePostRequest, Tier2ApiApiTier2DeclinePostRequest, Tier2ApiApiTier2RaisePostRequest, Tier2Model } from "../services/api";
import { useApiConfiguration } from "../services/api-configuration";
import { APIStatus } from "../utils/types";
import { toDate } from "date-fns";

export enum IGroupSort {
  NAME_DESC = "NAME_DESC",
  EFFECTIVE_DATE_ASC = "EFFECTIVE_DATE_ASC",
  EFFECTIVE_DATE_DESC = "EFFECTIVE_DATE_DESC",
  REQUESTED_DATE_ASC = "REQUESTED_DATE_ASC",
  REQUESTED_DATE_DESC = "REQUESTED_DATE_DESC",
  REISSUE_MONTH_ASC = "REISSUE_MONTH_ASC",
  REISSUE_MONTH_DESC = "REISSUE_MONTH_DESC",
}

export interface TimelineEvent {
  title?: string;
  createdBy: string;
  createdDate: Date;
  documents: {
    fileName: string;
    fileId: string;
  }[];
  notes?: string;
  status: string;
  type: 'RERATE' | 'TIER2' | 'HIGH_RISK';
}

export type AnyGroupRequest = (ReRateModel | Tier2Model) & { type: 'RERATE' | 'TIER2' | 'HIGH_RISK' };

export type State = {
  group: GroupServiceGetResponse | null;
  groupLoaded: boolean;
  rates: RateReliefCalculatorServiceResponse;
  originalRates: RateReliefCalculatorServiceResponse;
  apiState: APIStatus | null;
  ratesApiState: APIStatus | null;
  history: TimelineEvent[],
  historyApiState: APIStatus | null,
  reRateInputs: CalculatorApiApiCalculatorGetRequest;
}

export type Actions = {
  setGroup: State['group'];
}

const initialState: State = {
  group: null,
  groupLoaded: false,
  apiState: APIStatus.LOADING,
  rates: {},
  ratesApiState: null,
  originalRates: {},
  history: [],
  historyApiState: null,
  reRateInputs: {},
}

export const fetchGroup = createAsyncThunk(
  'group/fetchByPolicyNumber',
  async ({ policyNumber, timePeriodDate }: { policyNumber: string, timePeriodDate: Date }) => {
    const api = new GroupApi(useApiConfiguration());
    try {
      return await api.apiGroupGetGet({
        policyNumber,
        timePeriodDate: toDate(timePeriodDate),
      });
    } catch (e) {
      console.error('Error fetching group', e);
    }
  }
)

export async function fetchGroupCalculationForPeriod(req: CalculatorApiApiCalculatorGetRequest) {
  const api = new CalculatorApi(useApiConfiguration());
  return await api.apiCalculatorGet({
    ...req,
  });
}

export const fetchGroupRates = createAsyncThunk(
  'group/calculateRates',
  async (req: CalculatorApiApiCalculatorGetRequest) => {
    const { ui: { currentPeriod} } = store.getState();
    const timePeriodDate = req.timePeriodDate || currentPeriod;

    return await fetchGroupCalculationForPeriod({
      ...req,
      ...timePeriodDate && { timePeriodDate: toDate(timePeriodDate) },
    });
  }
)

export const resetGroupRates = createAsyncThunk(
  'group/resetCalculateRates',
  async (req: CalculatorApiApiCalculatorGetRequest) => {
    const { ui: { currentPeriod} } = store.getState();
    const timePeriodDate = req.timePeriodDate || currentPeriod;

    return await fetchGroupCalculationForPeriod({
      ...req,
      ...timePeriodDate && { timePeriodDate: toDate(timePeriodDate) },
    });
  }
)



export const fetchGroupRequestHistory = createAsyncThunk(
  'group/fetchRequestHistory',
  async({ policyNumber, timePeriodDate }: { policyNumber: string, timePeriodDate: Date }): Promise<TimelineEvent[]> => {

    const groupsApi = new GroupApi(useApiConfiguration());
    const reratesApi = new ReRatesApi(useApiConfiguration());
    const tier2Api = new Tier2Api(useApiConfiguration());
    const highRiskApi = new HighRiskApi(useApiConfiguration());

    const [eventsRes, retateRes, tier2Res, highRiskRes] = await Promise.all([
      groupsApi.apiGroupGetGroupEventsGet({ timePeriodDate, policyNumber }),
      reratesApi.apiReRatesGetGet({ timePeriodDate, policyNumber }),
      tier2Api.apiTier2GetGet({ timePeriodDate, policyNumber }),
      highRiskApi.apiHighRiskGetGet({ policyNumber }),
    ]);

    return [
      ...eventsRes || [],
      ...retateRes.reRates || [],
      ...tier2Res.tier2s || [],
      ...highRiskRes.highRisks || [],
    ].map((change) => {
      const type = retateRes.reRates?.includes(change) ? 'RERATE' :
                    tier2Res.tier2s?.includes(change) ? 'TIER2' : 
                    highRiskRes.highRisks?.includes(change) ? 'HIGH_RISK' :
                    'EVENT';

      switch (type) {
        case 'HIGH_RISK':
          return {
            title: 'High risk ' + (change as HighRiskModel).highRiskStatus,
            createdBy: (change as HighRiskModel).createdBy,
            createdDate: (change as HighRiskModel).createdDate,
            documents: [
              ...(change as HighRiskModel).fileName ? [{
                fileName: (change as HighRiskModel).fileName,
                fileId: (change as HighRiskModel).fileId,
              }] : [],
            ],
            notes: (change as HighRiskModel).note,
            status: (change as HighRiskModel).highRiskStatus,
            type,
          }
        case 'RERATE':
          return {
            title: 'Rerate ' + (change as ReRateModel).reRateStatus,
            createdBy: (change as ReRateModel).createdBy,
            createdDate: (change as ReRateModel).createdDate,
            documents: [
              ...(change as ReRateModel).fileName ? [{
                fileName: (change as ReRateModel).fileName,
                fileId: (change as ReRateModel).requestFileGUID,
              }] : [],
            ],
            notes: (change as ReRateModel).note,
            status: (change as ReRateModel).reRateStatus,
            type,
          }
        case 'TIER2':
          return {
            title: 'Tier 2 ' + (change as Tier2Model).tier2Status,
            createdBy: (change as Tier2Model).createdBy,
            createdDate: (change as Tier2Model).createdDate,
            documents: [
              ...(change as Tier2Model).fileName ? [{
                fileName: (change as Tier2Model).fileName,
                fileId: (change as Tier2Model).fileGUID,
              }] : [],
            ],
            notes: (change as Tier2Model).note,
            status: (change as Tier2Model).tier2Status,
            type,
          }
        default:
          return {
            title: 
              (change as GroupEventModel).type === 'AssignedTo' ? 'Group assigned to ' + (change as GroupEventModel).jsonData :
              (change as GroupEventModel).type === 'Completed' ? ((change as GroupEventModel).jsonData === 'True' ? 'Marked as complete' : 'Group re-opened') : 
              (change as GroupEventModel).type === 'ReRateAvailable' ? ((change as GroupEventModel).jsonData === 'True' ? 'Relief options turned on': 'Relief options turned off') 
              : '',
            createdBy: (change as GroupEventModel).createdBy,
            createdDate: (change as GroupEventModel).createdDate,
            status: (change as GroupEventModel).type === 'ReRateAvailable' ? ((change as GroupEventModel).jsonData === 'True' ? 'TurnedOn' : 'TurnedOff') : '',
            type,
          }
      }
    }).sort((a, b) => {
      return new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime() < 0 ? -1 : 1;
    });
  }
);

export async function submitRerateRequest(req: ReRatesApiApiReRatesRaisePostRequest) {
  const api = new ReRatesApi(useApiConfiguration());
  return await api.apiReRatesRaisePost(req);
}

export async function submitRerateResponse(req: ReRatesApiApiReRatesRaisePostRequest) {
  const api = new ReRatesApi(useApiConfiguration());
  return await api.apiReRatesCompletePost(req);
}

export async function submitHighRiskLockRequest(req: HighRiskApiApiHighRiskRaisePostRequest) {
  const api = new HighRiskApi(useApiConfiguration());
  return await api.apiHighRiskRaisePost(req);
}

export async function unlockGroup(req: HighRiskApiApiHighRiskRemovePostRequest) {
  const api = new HighRiskApi(useApiConfiguration());
  return await api.apiHighRiskRemovePost(req);
}

export async function submitHighRiskUnlockRequest(req: HighRiskApiApiHighRiskReviewPostRequest) {
  const api = new HighRiskApi(useApiConfiguration());
  return await api.apiHighRiskReviewPost(req);
}

export async function submitTier2Request(req: Tier2ApiApiTier2RaisePostRequest) {
  const api = new Tier2Api(useApiConfiguration());
  return await api.apiTier2RaisePost(req);
}

export async function acceptTier2Request(req: Tier2ApiApiTier2CompletePostRequest) {
  const api = new Tier2Api(useApiConfiguration());
  return await api.apiTier2CompletePost(req)
}

export async function declineTier2Request(req: Tier2ApiApiTier2DeclinePostRequest) {
  const api = new Tier2Api(useApiConfiguration());
  return await api.apiTier2DeclinePost(req)
}

export const markGroupAsComplete = createAsyncThunk(
  'group/markAsComplete',
  async ({ groupId, completed, timePeriodDate }: { groupId: string, completed: boolean, timePeriodDate: Date }) => {
    const api = new GroupApi(useApiConfiguration());
    await api.apiGroupSetCompletedPost({
      groupId,
      timePeriodDate,
      completed,
    });

    return await api.apiGroupGetGet({
      policyNumber: groupId,
      timePeriodDate,
    });
  }
);

export const assignGroup = createAsyncThunk(
  'group/assignToMe',
  async ({ groupId, timePeriodDate }: { groupId: string, timePeriodDate: Date }) => {
    const api = new GroupApi(useApiConfiguration());

    await api.apiGroupAssignToMePost({
      groupId,
      timePeriodDate,
    });

    return await api.apiGroupGetGet({
      policyNumber: groupId,
      timePeriodDate,
    });
  }
);

export async function toggleRateReliefAvailability(groupId: string, availableForRR: boolean, timePeriodDate: Date) {
  const api = new GroupApi(useApiConfiguration());
  return await api.apiGroupSetAvaialableForRRPost({
    groupId,
    avaiableForRR: availableForRR,
    timePeriodDate,
  })
}

export const groupStore = createSlice({
    name: 'group',
    initialState,
    reducers: {
      setRerateInputs: (state: State, action: { payload: CalculatorApiApiCalculatorGetRequest}) => {
        state.reRateInputs = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder.addCase(fetchGroup.fulfilled, (state, action) => {
        state.apiState = APIStatus.COMPLETE;
        state.group = action.payload;
        state.groupLoaded = true;
      }).addCase(fetchGroup.pending, (state) => {
        if (!state.group) {
          state.apiState = APIStatus.LOADING;
          state.group = null;
        }
      }).addCase(fetchGroup.rejected, (state) => {
        state.apiState = APIStatus.ERROR;
        state.group = null;
      });

      builder.addCase(resetGroupRates.fulfilled, (state, action: { payload: RateReliefCalculatorServiceResponse }) => {
        state.rates = action.payload;
        state.originalRates = action.payload;
        state.ratesApiState = APIStatus.COMPLETE;
      }).addCase(resetGroupRates.pending, (state) => {
        state.ratesApiState = APIStatus.LOADING;
      }).addCase(resetGroupRates.rejected, (state) => {
        state.ratesApiState = APIStatus.ERROR;
      });

      builder.addCase(fetchGroupRates.pending, (state) => {
        state.ratesApiState = APIStatus.LOADING;
      }).addCase(fetchGroupRates.rejected, (state) => {
        state.ratesApiState = APIStatus.ERROR;
      }).addCase(fetchGroupRates.fulfilled, (state, action: { payload: RateReliefCalculatorServiceResponse }) => {
        state.rates = action.payload;
        state.groupLoaded = true;

        if (!Object.keys(state.originalRates).length) state.originalRates = action.payload;
        state.ratesApiState = APIStatus.COMPLETE;
      });

      builder.addCase(fetchGroupRequestHistory.pending, (state) => {
        state.historyApiState = APIStatus.LOADING;
      }).addCase(fetchGroupRequestHistory.rejected, (state) => {
        state.historyApiState = APIStatus.ERROR;
      }).addCase(fetchGroupRequestHistory.fulfilled, (state, action: { payload: AnyGroupRequest[]}) => {
        state.history = action.payload;
        state.historyApiState = APIStatus.COMPLETE;
      });

      builder.addCase(assignGroup.fulfilled, (state, action) => {
        if (state.group) {
          state.group.assignedTo = action.payload.assignedTo;
        }
      });

      builder.addCase(markGroupAsComplete.fulfilled, (state, action) => {
        if (state.group) {
          state.group = action.payload;
        }
      })
    }
});

export const {
  setRerateInputs,
} = groupStore.actions;

export default groupStore.reducer;